import React, { useEffect, useRef, useState } from "react";
import { passwordCriteria } from "../../utils/password-policy";

// How long a just-met rule stays visible as a green tick before it starts
// collapsing away, and how long that collapse transition itself takes. Keep
// FADE_MS in sync with the Tailwind `duration-300` class below.
const HOLD_MS = 600;
const FADE_MS = 300;

type Phase = "visible" | "collapsing" | "gone";

/**
 * Live checklist of the password rules, shared by every screen that sets one.
 * Only shows what's still unmet, wrapped onto as few lines as fit. When a
 * rule becomes met it briefly shows a green tick, then collapses smoothly
 * away instead of vanishing instantly - so it reads as "confirmed, then
 * cleared" rather than an abrupt jump. A fully-met password renders nothing.
 */
const PasswordCriteria: React.FC<{ password: string }> = ({ password }) => {
  const criteria = passwordCriteria(password);
  const [phases, setPhases] = useState<Record<string, Phase>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    criteria.forEach((c) => {
      if (!c.met) {
        // Unmet again (e.g. the user deleted characters) - cancel any
        // pending collapse and show it plainly again.
        clearTimeout(timers.current[c.label]);
        delete timers.current[c.label];
        setPhases((prev) => (prev[c.label] ? { ...prev, [c.label]: "visible" } : prev));
        return;
      }
      // Met - hold as a green tick, then collapse. Guarded on the actual
      // phase value (not mere key presence: the unmet branch above already
      // populates phases[c.label] = "visible" the first time a rule is
      // un-met, so a presence check would permanently skip every
      // met->unmet->met cycle after the first one - the exact case a user
      // backspacing mid-password hits constantly) and on there being no
      // timer already in flight, so a fast-typing user doesn't stack
      // duplicate timers for the same rule.
      const currentPhase = phases[c.label];
      if (currentPhase !== "collapsing" && currentPhase !== "gone" && !timers.current[c.label]) {
        timers.current[c.label] = setTimeout(() => {
          setPhases((prev) => ({ ...prev, [c.label]: "collapsing" }));
          timers.current[c.label] = setTimeout(() => {
            setPhases((prev) => ({ ...prev, [c.label]: "gone" }));
            delete timers.current[c.label];
          }, FADE_MS);
        }, HOLD_MS);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  useEffect(() => () => {
    Object.values(timers.current).forEach(clearTimeout);
  }, []);

  const items = criteria.filter((c) => phases[c.label] !== "gone");
  if (items.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-y-1">
      {items.map((c) => {
        const collapsing = phases[c.label] === "collapsing";
        return (
          <div
            key={c.label}
            className={`flex items-center gap-1.5 overflow-hidden transition-all duration-300 ${
              collapsing ? "opacity-0 max-w-0 mr-0" : "opacity-100 max-w-[240px] mr-3"
            }`}
          >
            <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${c.met ? "bg-green-500" : "bg-slate-200"}`}>
              {c.met && (
                <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <span className={`text-xs whitespace-nowrap transition-colors ${c.met ? "text-green-600" : "text-slate-400"}`}>{c.label}</span>
          </div>
        );
      })}
    </div>
  );
};

export default PasswordCriteria;
